/**
 * The persisted login session. Deliberately free of Redux so the public
 * landing page can check "is someone already signed in?" without pulling the
 * store (and the rest of the app) into its bundle.
 */
export interface PersistedAuth {
  token: string | null;
  staffId: string | null;
  email: string | null;
  /**
   * `demo` for a "Try live demo" session, `salesman` for a field salesman's (his id is
   * `staffId`, and he has no email); a session without it is staff.
   */
  role?: 'demo' | 'salesman';
  /**
   * The last session was a demo and it has ended (expired token, 401, or "Exit demo"). There's
   * no account to log back into, so protected routes send the visitor to the landing page
   * rather than /login. Cleared by the next sign-in.
   */
  endedDemo?: true;
}

const STORAGE_KEY = 'orderbot.auth';

const EMPTY: PersistedAuth = { token: null, staffId: null, email: null };

/** So login survives a page reload — localStorage is used only for this one slice. */
export const readPersistedAuth = (): PersistedAuth => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY };
    return JSON.parse(raw) as PersistedAuth;
  } catch {
    return { ...EMPTY };
  }
};

export const writePersistedAuth = (state: PersistedAuth): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage blocked (private window) — the session will only last the tab, no crash
  }
};
